'use client'

/**
 * SiteFooter — the site's first footer, and the cheap half of the internal-linking problem.
 *
 * WHY: ~40 public SEO pages rendered NO internal link cluster and the site had no footer at all, so
 * those pages were dead ends for both readers and crawlers. Adding <MoneyPageLinks /> to 40 files is a
 * large blast radius, and two obvious targets (/guides, /user-guide) are CLIENT components that cannot
 * import a server component at all. One footer in the root layout covers every page including those.
 *
 * ⛔ IT MUST NOT APPEAR ON THE FULL-BLEED SURFACES. /chart/[symbol], /stream, /game and the embed views
 * are working UIs that fill the viewport; a footer under them is at best noise and at worst covers the
 * thing someone is trading from. Hidden by route, using the same usePathname convention ConditionalMain
 * already uses for noNavbarRoutes rather than inventing a second mechanism.
 *
 * ⚠ VERIFY BY CURLING THE RENDERED PAGE, NOT BY READING THIS FILE. A source grep is what produced the
 * wrong link count the first time this problem was measured.
 */

import Link from 'next/link'
import { usePathname } from 'next/navigation'

/** Full-bleed / app-like surfaces. Prefix match, so /chart/NVDA and /chart/NVDA/trades both hide. */
const HIDE_PREFIXES = ['/chart', '/stream', '/game', '/labs/workspace', '/embed']

const COLUMNS: { title: string; links: { href: string; label: string }[] }[] = [
  {
    title: 'Start here',
    links: [
      { href: '/site-guide', label: 'Site Guide' },
      { href: '/top-stocks', label: 'Top Stocks Today' },
      { href: '/how-it-works', label: 'How It Works' },
      { href: '/guides', label: 'Trading Guides' },
    ],
  },
  {
    title: 'Scanners',
    links: [
      { href: '/stock-scanners', label: 'All Scanners' },
      { href: '/biggest-gainers-today', label: 'Biggest Gainers' },
      { href: '/premarket-movers', label: 'Premarket Movers' },
      { href: '/52-week-highs', label: '52-Week Highs' },
      // ⭐ ADDED 2026-08-16 TO WIN ONE SPECIFIC QUERY. "halt scanner" is 423 impressions — the
      // LARGEST NON-BRANDED QUERY ON THE SITE — and /halt-scanner sat at position 15.7 on its own
      // exact-match term, earning 6 clicks. Top-5 on that one query is worth ~40-60 clicks/month
      // against a non-branded site TOTAL of 72. "gap scanner" is smaller but /gap-scanner ranked 42.5,
      // i.e. effectively not ranking at all despite the page existing.
      //
      // The hypothesis is internal-link equity, and the ordering was exact across all three scanner
      // pages: /rvol-scanner 22 inbound links → position 5.7, /halt-scanner 13 → 15.7, /gap-scanner
      // 7 → 42.5. None was in this footer, which is the highest-equity link position on the site
      // because it renders on every page.
      //
      // 🚨 /rvol-scanner IS DELIBERATELY *NOT* ADDED HERE, AND MUST NOT BE. It is the CONTROL: already
      // well-linked, already ranking 5.7 and converting at 16.44%. Adding it would remove the only
      // page that can tell us whether the footer link did anything or whether these two moved for
      // unrelated reasons. Read all three at the ~2026-09-08 export ([[halt-scanner-is-the-biggest-winnable-query]]).
      // If halt and gap climb while rvol holds, internal links are the lever. If nothing moves, the
      // link-count correlation was confounded (n=3, and confounded with page age) and should be dropped.
      { href: '/halt-scanner', label: 'Halt Scanner' },
      { href: '/gap-scanner', label: 'Gap Scanner' },
    ],
  },
  {
    title: 'Research',
    links: [
      // ⛔ RESEARCH, NOT SCANNERS, deliberately. The Scanners column carries the /halt-scanner vs
      // /gap-scanner vs /rvol-scanner link-equity experiment; a Research-column entry cannot
      // confound it. Held back from the 09-05 ship because SiteFooter.tsx was hash-locked until
      // 2026-09-08 and "probably fine" does not beat an explicit lock - added 09-09, the day after
      // it expired. See [[readd-track-record-footer-link-after-0908]].
      { href: '/track-record', label: 'Track Record' },
      { href: '/due-diligence', label: 'Due Diligence' },
      { href: '/ema-slope', label: 'EMA Slope Rankings' },
      { href: '/super-portfolio', label: 'Super Portfolio' },
      { href: '/money-map', label: 'The Money Map' },
      { href: '/calculators', label: 'Calculators' },
    ],
  },
  {
    title: 'More',
    links: [
      { href: '/taxes', label: 'Trading Taxes' },
      { href: '/api-docs', label: 'API Docs' },
      { href: '/subscribe', label: 'Subscribe' },
      // ⭐ ADDED 2026-08-15. The partner program had been viewed ONCE, ever, against 61,938 tracked
      //    page views: its only entry points were a collapsed 10-item Resources dropdown, /api-docs
      //    and /developers. A footer is where people actually look for an affiliate programme, it
      //    renders on /alerts (where 167 of 223 monthly-active users go), and it is a crawlable
      //    internal link, which the page needs now that it finally has content worth indexing.
      //    Deliberately NOT a banner: subscribers are promised freedom from nudges, and they are
      //    also the 20% tier, so the entry point has to reach them without spending that promise.
      { href: '/affiliate', label: 'Partner Program' },
      { href: '/support', label: 'Support' },
    ],
  },
]

export default function SiteFooter() {
  const pathname = usePathname() || ''
  if (HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))) return null

  return (
    <footer className="mt-10 border-t border-border bg-card">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          {COLUMNS.map((c) => (
            <div key={c.title}>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{c.title}</div>
              {/**
                * ⚠ `inline-block py-1 -my-1` IS A TAP TARGET, NOT DECORATION, AND IT IS VISUALLY
                * INERT (register: `mobile-tap-targets-under-24px`). Measured on the LIVE site at
                * 375px: these 20 links render 16px tall, under the 24px minimum in WCAG 2.5.8, and
                * they were the single largest group of undersized targets on the site because this
                * footer renders on every page.
                *
                * The padding grows the hit area to 24px; the matching negative margin removes it
                * again from the layout box, so the rendered spacing does not move. The link has no
                * background, so nothing repaints.
                *
                * ⛔ py-1 AND NOT py-2, DELIBERATELY. The gap here is space-y-1.5 (6px). Growing 4px
                * in each direction leaves 2px of clearance; 8px would make adjacent hit areas touch
                * or overlap, and overlapping targets mean tapping the wrong link, which is worse
                * than a small one.
                *
                * ⭐ SAFE AGAINST THE FOOTER SEO EXPERIMENT ([[seo-experiments-running-do-not-touch]]).
                * That test is about WHICH LINKS EXIST here - /halt-scanner and /gap-scanner added,
                * /rvol-scanner deliberately withheld as the control. No href, label or ordering is
                * touched, so the internal-link graph it measures is unchanged.
                */}
              <ul className="space-y-1.5">
                {c.links.map((l) => (
                  <li key={l.href}>
                    <Link href={l.href} prefetch={false} className="inline-block py-1 -my-1 text-[13px] text-muted-foreground transition-colors hover:text-amber-700 dark:hover:text-amber-300">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-4 text-[11px] text-muted-foreground">
          {/* The brand runs an active X account that out-reaches Reddit most days, but it was invisible
              on-page (E-E-A-T audit 2026-08-24: external validation was near-empty). Surfacing it here
              is a verifiable authority signal AND a path to an owned audience. rel="me" is deliberate:
              with a link back from the X profile it VERIFIES the profile belongs to this site (the
              rel-me identity convention), which is a stronger signal than a bare link. */}
          <a
            href="https://x.com/thedesperatetrdr"
            target="_blank"
            rel="me noopener noreferrer"
            aria-label="Follow TheDesperateTrader on X"
            className="inline-flex items-center gap-1.5 py-1 -my-1 font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3 w-3 fill-current">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Follow on X
          </a>
          <span>&copy; {new Date().getFullYear()} TheDesperateTrader</span>
          <span>Educational and informational only, not financial advice.</span>
          <Link href="/privacy-policy" prefetch={false} className="inline-block py-1 -my-1 hover:text-foreground">Privacy</Link>
          <Link href="/terms" prefetch={false} className="inline-block py-1 -my-1 hover:text-foreground">Terms</Link>
          {/* ⭐ THESE THREE WERE ORPHANED. Found 2026-08-15 by a site-guide coverage audit: /about,
              /corrections and /editorial-policy are in the sitemap, so Google is told they exist,
              and nothing OUTSIDE those three linked to any of them. They referenced each other, so
              each looked linked in a naive grep: an ISLAND, not three loose pages. That is the
              harder shape to spot and the reason the audit measured inbound links specifically.
              ⚠ THAT DEFEATS THEIR ENTIRE PURPOSE. They are trust pages: who is behind this site,
              what the editorial standards are, and how a published error gets corrected. A trust
              page nobody can reach builds no trust. Google News Publisher Center also expects them
              to be discoverable, and that submission is still open.
              ⛔ FOOTER, NOT THE MAIN NAV, DELIBERATELY. The nav is being made SHORTER for new
              traders; three policy links belong where readers already look for them. */}
          <Link href="/about" prefetch={false} className="inline-block py-1 -my-1 hover:text-foreground">About</Link>
          <Link href="/editorial-policy" prefetch={false} className="inline-block py-1 -my-1 hover:text-foreground">Editorial Policy</Link>
          <Link href="/corrections" prefetch={false} className="inline-block py-1 -my-1 hover:text-foreground">Corrections</Link>
        </div>
      </div>
    </footer>
  )
}
